import { useState } from 'react';
import { Moon, Sun, Settings, LogOut, Headphones } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import SupportModal from './SupportModal';

const Header = () => {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
    } catch (error) {
      // Logout error handled silently
    } finally {
      setIsLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-gray-200 dark:border-white/[0.06] bg-white/80 dark:bg-[#0a0a0a]/80 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 sm:px-6 md:px-8 lg:px-6 py-3 sm:py-4">
          {/* Left side - spacer for mobile menu button on small screens */}
          <div className="w-14 sm:w-16 lg:w-0"></div>

          {/* Center/Right - Action buttons */}
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="btn-touch p-2 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-[10px] transition-colors duration-200"
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>

            {/* Support Button */}
            <button
              onClick={() => setShowSupportModal(true)}
              className="btn-touch p-2 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-[10px] transition-colors duration-200"
              title="Contact Support"
            >
              <Headphones className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>

            {/* Notifications */}
            <NotificationBell />

            {/* Settings - Hide on small mobile */}
            <button
              onClick={() => {
                navigate('/dashboard/general');
              }}
              className="hidden xs:flex btn-touch p-2 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-[10px] transition-colors duration-200"
              title="Settings"
            >
              <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>

            {/* User Info - Desktop Only */}
            <div className="hidden md:flex items-center gap-3 lg:gap-4 ml-2">
              <div className="text-right hidden lg:block">
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[150px]">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500 truncate max-w-[150px]">{user?.email}</div>
              </div>

              {/* Vertical Divider */}
              <div className="h-8 w-px bg-gray-200 dark:bg-white/[0.06] hidden lg:block"></div>

              {/* Logout Button */}
              <button
                onClick={() => setShowLogoutModal(true)}
                className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-[10px] transition-all duration-200"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden lg:inline">Logout</span>
              </button>
            </div>

            {/* Mobile Logout - moved to sidebar */}
          </div>
        </div>
      </header>

      {/* Support Modal */}
      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowLogoutModal(false)}
          />
          <div className="relative bg-white/80 dark:bg-[#111]/90 backdrop-blur-xl rounded-[20px] border border-gray-200 dark:border-white/[0.1] shadow-2xl w-full max-w-xs">
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-red-500/[0.08] rounded-[14px] flex items-center justify-center mx-auto mb-4">
                <LogOut className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                Log out?
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-light">
                You'll need to sign in again to access your dashboard.
              </p>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 py-2.5 text-sm font-medium bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-full transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex-1 py-2.5 text-sm font-medium bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors disabled:opacity-50"
              >
                {isLoggingOut ? 'Logging out...' : 'Log out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;